import {
  createFileRoute,
  Link as RouterLink,
  useNavigate,
} from '@tanstack/react-router';
import {
  Box,
  Input,
  Button,
  Typography,
  Container,
  Alert,
  Card,
  FormControl,
  FormLabel,
  FormHelperText,
  Link,
} from '@mui/joy';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '@/services/axios';
import { pageTitle } from '@/utils/pageTitle';

/**
 * Backend: FluentValidation (ResetPasswordRequestValidator) exige mínimo 6 caracteres;
 * UserManager.ResetPasswordAsync aplica además la política por defecto de ASP.NET Core Identity
 * (mayúscula, minúscula, dígito y carácter no alfanumérico), no personalizada en AddIdentity.
 */
const newPasswordField = z
  .string()
  .min(6, 'La contraseña debe tener al menos 6 caracteres')
  .regex(/[a-z]/, 'Debe incluir al menos una letra minúscula')
  .regex(/[A-Z]/, 'Debe incluir al menos una letra mayúscula')
  .regex(/\d/, 'Debe incluir al menos un número')
  .regex(/[^a-zA-Z0-9]/, 'Debe incluir al menos un carácter especial');

const schema = z
  .object({
    newPassword: newPasswordField,
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export const Route = createFileRoute('/_public/reset-password')({
  component: ResetPasswordPage,
  validateSearch: z.object({
    email: z.string().optional().default(''),
    token: z.string().optional().default(''),
  }),
  head: () => pageTitle('Restablecer contraseña'),
});

function ResetPasswordPage() {
  const { email, token } = Route.useSearch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (newPassword: string) =>
      api.post('/auth/reset-password', {
        email,
        token,
        newPassword,
      }),
    onSuccess: () => {
      void navigate({ to: '/login' });
    },
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate(data.newPassword);
  };

  const missingParams = !email || !token;

  return (
    <Container maxWidth='xs'>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, p: 4, boxShadow: 'lg' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography level='h2' fontWeight={500}>
              Nueva contraseña
            </Typography>
            <Typography level='body-md'>
              Elija una contraseña nueva para su cuenta
            </Typography>
            <Typography
              level='title-sm'
              sx={{ mt: 2, mb: 0.5, textAlign: 'left', width: '100%' }}
            >
              Requisitos
            </Typography>
            <Typography
              level='body-sm'
              component='ul'
              sx={{
                pl: 2.5,
                m: 0,
                textAlign: 'left',
                width: '100%',
                color: 'neutral.600',
              }}
            >
              <li>Al menos 6 caracteres</li>
              <li>Al menos una letra mayúscula y una minúscula</li>
              <li>Al menos un número</li>
              <li>Al menos un símbolo (por ejemplo !, @, #)</li>
            </Typography>
          </Box>

          {missingParams && (
            <Alert color='warning' sx={{ mb: 2 }}>
              El enlace no es válido o está incompleto. Solicite un nuevo correo
              desde &quot;Olvidé mi contraseña&quot;.
            </Alert>
          )}

          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Controller
              name='newPassword'
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.newPassword}>
                  <FormLabel>Nueva contraseña</FormLabel>
                  <Input
                    {...field}
                    type='password'
                    placeholder='••••••••'
                    required
                    disabled={mutation.isPending || missingParams}
                    autoComplete='new-password'
                    autoFocus
                  />
                  {errors.newPassword && (
                    <FormHelperText>{errors.newPassword.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name='confirmPassword'
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.confirmPassword}>
                  <FormLabel>Confirmar contraseña</FormLabel>
                  <Input
                    {...field}
                    type='password'
                    placeholder='••••••••'
                    required
                    disabled={mutation.isPending || missingParams}
                    autoComplete='new-password'
                  />
                  {errors.confirmPassword && (
                    <FormHelperText>
                      {errors.confirmPassword.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {mutation.isError && (
              <Alert color='danger'>
                {(() => {
                  const err = mutation.error as AxiosError<{
                    errors?: Record<string, string>;
                  }>;
                  const data = err?.response?.data;
                  if (data?.errors) {
                    const e = data.errors;
                    return e.Error || e[Object.keys(e)[0]] || 'No se pudo restablecer la contraseña.';
                  }
                  return 'No se pudo restablecer la contraseña.';
                })()}
              </Alert>
            )}

            <Button
              type='submit'
              fullWidth
              loading={mutation.isPending}
              variant='outlined'
              color='neutral'
              disabled={missingParams}
              sx={{ mt: 1 }}
            >
              Guardar contraseña
            </Button>

            <Typography level='body-sm' sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to='/login'
                sx={{ color: 'text.primary' }}
              >
                Volver al inicio de sesión
              </Link>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}
